// src/app/api/contactApi.ts
import { baseApi } from "./baseApi";

// ── Types ──────────────────────────────────────────────────────────────────

export type ContactStatus = "new" | "read" | "replied" | "archived";

export type ProjectType =
  | "Web Development"
  | "Mobile App"
  | "Tech Product"
  | "Consulting"
  | "Other"
  | "";

export type BudgetRange =
  | "< \u20a61M"
  | "\u20a61M \u2013 \u20a65M"
  | "\u20a65M \u2013 \u20a620M"
  | "\u20a620M+"
  | "Let's discuss"
  | "";

export interface ContactRecord {
  id: string;
  name: string;
  email: string;
  company: string;
  phone: string;
  projectType: ProjectType;
  budget: BudgetRange;
  timeline: string;
  message: string;
  status: ContactStatus;
  confirmationSent: boolean;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

// ── Request shapes ─────────────────────────────────────────────────────────

export interface SubmitContactRequest {
  name: string;
  email: string;
  company?: string;
  phone?: string;
  projectType?: ProjectType;
  budget?: BudgetRange;
  timeline?: string;
  message: string;
}

export interface GetContactsRequest {
  status?: ContactStatus;
  search?: string;
  page?: number;
  limit?: number;
}

export interface UpdateContactStatusRequest {
  id: string;
  status: ContactStatus;
  notes?: string;
}

// ── Response shapes ────────────────────────────────────────────────────────

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: Pagination;
}

// ── Injected endpoints ─────────────────────────────────────────────────────

export const contactApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Submit a new contact / project brief
     * POST /api/contact — no auth required
     */
    submitContact: builder.mutation<
      ApiResponse<ContactRecord>,
      SubmitContactRequest
    >({
      query: (body) => ({
        url: "/contact",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Contact", id: "LIST" }],
    }),

    /**
     * Get all contact submissions — admin only
     * GET /api/contact?status=new&search=&page=1&limit=20
     */
    getContacts: builder.query<
      PaginatedResponse<ContactRecord>,
      GetContactsRequest
    >({
      query: ({ status, search, page = 1, limit = 20 } = {}) => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        if (search) params.set("search", search);
        params.set("page", String(page));
        params.set("limit", String(limit));
        return `/contact?${params.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "Contact" as const,
                id,
              })),
              { type: "Contact", id: "LIST" },
            ]
          : [{ type: "Contact", id: "LIST" }],
    }),

    /**
     * Get a single contact submission — admin only
     * GET /api/contact/:id  (server auto-marks as "read")
     */
    getContactById: builder.query<ApiResponse<ContactRecord>, string>({
      query: (id) => `/contact/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Contact", id }],
    }),

    /**
     * Update contact status + optional admin notes — admin only
     * PATCH /api/contact/:id/status
     */
    updateContactStatus: builder.mutation<
      ApiResponse<ContactRecord>,
      UpdateContactStatusRequest
    >({
      query: ({ id, status, notes }) => ({
        url: `/contact/${id}/status`,
        method: "PATCH",
        body: { status, ...(notes !== undefined && { notes }) },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Contact", id },
        { type: "Contact", id: "LIST" },
      ],
    }),

    /**
     * Delete a contact submission — admin only
     * DELETE /api/contact/:id
     */
    deleteContact: builder.mutation<ApiResponse<null>, string>({
      query: (id) => ({
        url: `/contact/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Contact", id },
        { type: "Contact", id: "LIST" },
      ],
    }),
  }),

  overrideExisting: false,
});

export const {
  useSubmitContactMutation,
  useGetContactsQuery,
  useGetContactByIdQuery,
  useUpdateContactStatusMutation,
  useDeleteContactMutation,
} = contactApi;